import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type ApiError, type GetPipelineFunnelResult } from '@whinerp/client';
import { ErrorState, Funnel, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatDate } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Where the pipeline leaks (F-100, G-207).
 *
 * The bar for each stage is how many deals ENTERED it, and the line beneath is
 * what share of those went on. Counting what is sitting in each stage today
 * would give a shape that looks like a funnel and answers nothing: a stage
 * everybody passes through in a day looks empty, and therefore looks broken.
 *
 * The record starts when it starts. `deal_stage_history` was added long after
 * the deals were, so a window reaching further back than the first recorded
 * entry is a window the funnel cannot speak for - and a chart that drew a
 * confident 0% over it would be telling somebody their pipeline converts
 * nothing. That is said, above the chart, whenever it is true.
 */

const WINDOWS = [90, 365, 0] as const;

export function PipelineFunnelPanel({ pipelineId }: { pipelineId?: string }): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { isEnabled } = useSession();
  const [days, setDays] = useState<number>(90);
  /*
   * The funnel is a Growth feature and a workspace can switch it off. Asked
   * before the request rather than after it: a panel that fires a request it
   * is not entitled to make renders "Something went wrong" over a feature that
   * is merely not included, which tells somebody their product is broken when
   * it is working exactly as their plan says.
   */
  const entitled = isEnabled('F-100');

  /*
   * How far back, not which dates (D-354).
   *
   * The server works the window out, because it knows which day it is in the
   * workspace and this screen does not: a reader in London looking at a
   * workspace in Dubai computes a different "today" for part of every evening,
   * and the chart would quietly lose a day off the end of the window.
   */
  const funnel = useQuery<GetPipelineFunnelResult, ApiError>({
    enabled: entitled,
    queryKey: ['pipeline-funnel', pipelineId ?? null, days],
    queryFn: () =>
      api.getPipelineFunnel({
        query: {
          ...(pipelineId === undefined ? {} : { pipelineId }),
          // 0 is the "all time" window; an explicit from says so.
          ...(days === 0 ? { from: '2000-01-01' } : { days }),
        },
      }),
    // The window selector must survive being used: without this the whole
    // panel drops to its skeleton and unmounts the control mid-use.
    placeholderData: keepPreviousData,
  });

  if (!entitled) return null;
  if (funnel.isPending) return <Skeleton className="h-block-md w-full" />;
  if (funnel.isError) {
    return (
      <ErrorState title={t('state.error')} body={funnel.error.message} onRetry={() => void funnel.refetch()} />
    );
  }

  const data = funnel.data;
  const rows = data.stages.map((stage) => {
    /*
     * One binding feeds the bar's width, its label and the attribute a test
     * reads. Three expressions reading three fields would let the displayed
     * number drift from the width, and would let a test assert against a value
     * nobody sees.
     */
    const shown = stage.entered;
    return {
      id: stage.stageId,
      label: stage.label,
      weight: shown,
      value: t('funnel.entered', { values: { count: formatCount(shown, format) } }),
      aside:
        stage.conversionPercent === null
          ? undefined
          : t('funnel.conversion', { values: { percent: stage.conversionPercent } }),
      attrs: { 'data-funnel-stage': stage.stageId, 'data-funnel-count': String(shown) },
    };
  });

  return (
    <Panel data-region="pipeline-funnel">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <SectionLabel>{t('funnel.title')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('funnel.body')}</p>
          </div>
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('funnel.window.90')}
            <select
              data-funnel-window
              className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
              value={String(days)}
              onChange={(event) => setDays(Number(event.target.value))}
            >
              {WINDOWS.map((option) => (
                <option key={option} value={String(option)}>
                  {option === 0
                    ? t('funnel.window.all')
                    : option === 365
                      ? t('funnel.window.365')
                      : t('funnel.window.90')}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* What the record cannot speak for, said before the chart rather than
            under it. Drawn only when it is true. */}
        {data.historyStartsOn === null ? (
          <p data-funnel-no-history className="rounded-control bg-sunken px-3 py-2 text-dense text-ink-meta">
            {t('funnel.noHistory')}
          </p>
        ) : data.windowPrecedesHistory ? (
          <p
            data-funnel-before-history
            className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
          >
            {t('funnel.beforeHistory', {
              values: { date: formatDate(data.historyStartsOn, format) },
            })}
          </p>
        ) : (
          <p data-funnel-since className="text-meta text-ink-meta">
            {t('funnel.since', { values: { date: formatDate(data.historyStartsOn, format) } })}
          </p>
        )}

        <Funnel rows={rows} />

        {/* The velocity row beneath each stage, which is the half a bar chart
            cannot show: a stage can convert well and still take a quarter. */}
        <ul className="flex flex-col">
          {data.stages.map((stage) => (
            <li
              key={stage.stageId}
              data-funnel-detail={stage.stageId}
              className="flex flex-wrap items-baseline gap-3 border-t border-t-rule py-1 first:border-t-0"
            >
              <span className="min-w-0 flex-1 truncate text-meta text-ink">{stage.label}</span>
              <span data-funnel-conversion={stage.stageId} className="text-meta text-ink-meta">
                {stage.conversionPercent === null
                  ? stage.outcome === 'open'
                    ? t('funnel.conversion.none')
                    : t('funnel.conversion.terminal')
                  : t('funnel.advanced', { values: { count: formatCount(stage.advanced, format) } })}
              </span>
              <span data-funnel-days={stage.stageId} className="text-meta text-ink-meta">
                {stage.averageDays === null ? (
                  t('funnel.days.none')
                ) : (
                  <Num>{t('funnel.days', { values: { days: stage.averageDays } })}</Num>
                )}
              </span>
              {stage.stillHere > 0 && stage.averageDays !== null ? (
                <span data-funnel-still-here={stage.stageId} className="text-meta text-ink-meta">
                  {t('funnel.stillHere', { values: { count: formatCount(stage.stillHere, format) } })}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
